/**
 * Variable 配置块 JSON Schema 定义
 */
export const variableSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/variable.json',
  title: 'Variable 配置',
  description: 'Variable（变量）配置数据结构',
  type: 'object',
  required: ['variable_id', 'name', 'var_type', 'scope'],
  properties: {
    variable_id: {
      type: 'string',
      title: 'Variable ID',
      description: 'Variable 唯一标识符',
      pattern: '^var_[a-zA-Z0-9_]+$',
      minLength: 5,
      maxLength: 50,
      examples: ['var_evidence_collected', 'var_player_health', 'var_alert_level'],
    },
    name: {
      type: 'string',
      title: '变量名称',
      description: '变量的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['证据收集计数', '玩家生命值', '警戒等级'],
    },
    description: {
      type: 'string',
      title: '变量描述',
      description: '变量的详细描述',
      minLength: 0,
      maxLength: 500,
    },
    var_type: {
      type: 'string',
      title: '变量类型',
      description: '变量的数据类型',
      enum: ['integer', 'float', 'string', 'boolean', 'array', 'object'],
    },
    scope: {
      type: 'string',
      title: '作用域',
      description: '变量的作用域',
      enum: ['global', 'player', 'npc', 'faction', 'location', 'session'],
    },
    initial_value: {
      title: '初始值',
      description: '变量的初始值',
      oneOf: [
        { type: 'integer' },
        { type: 'number' },
        { type: 'string' },
        { type: 'boolean' },
        { type: 'array' },
        { type: 'object' },
        { type: 'null' },
      ],
    },
    min_value: {
      type: 'number',
      title: '最小值',
      description: '数值类型变量的最小值',
    },
    max_value: {
      type: 'number',
      title: '最大值',
      description: '数值类型变量的最大值',
    },
    allowed_values: {
      type: 'array',
      title: '允许的值',
      description: '变量允许的值列表（枚举）',
      items: {
        oneOf: [
          { type: 'string' },
          { type: 'number' },
          { type: 'boolean' },
        ],
      },
    },
    persistent: {
      type: 'boolean',
      title: '持久化',
      description: '是否需要持久化存储',
      default: false,
    },
    sync_to_client: {
      type: 'boolean',
      title: '同步到客户端',
      description: '是否需要同步到客户端',
      default: false,
    },
    category: {
      type: 'string',
      title: '分类',
      description: '变量的分类标签',
      enum: [
        '游戏状态',
        '玩家属性',
        'NPC 状态',
        '剧情进度',
        '环境变量',
        '系统设置',
        '其他',
      ],
    },
    read_only: {
      type: 'boolean',
      title: '只读',
      description: '是否为只读变量',
      default: false,
    },
    change_listeners: {
      type: 'array',
      title: '变化监听器',
      description: '当变量值变化时触发的事件',
      items: {
        type: 'object',
        required: ['event_id'],
        properties: {
          event_id: {
            type: 'string',
            title: '事件 ID',
            pattern: '^event_[a-zA-Z0-9_]+$',
          },
          condition: {
            type: 'string',
            title: '触发条件',
            description: '监听器触发的条件表达式',
          },
        },
      },
    },
    reset_on: {
      type: 'string',
      title: '重置时机',
      description: '变量重置的时机',
      enum: ['never', 'session_start', 'game_start', 'daily', 'custom'],
    },
    format: {
      type: 'object',
      title: '格式化',
      description: '变量值的格式化规则',
      properties: {
        display_format: {
          type: 'string',
          title: '显示格式',
          description: '用于显示的格式字符串',
          examples: ['{value}%', '{value} 个', '等级 {value}'],
        },
        unit: {
          type: 'string',
          title: '单位',
          description: '变量的单位',
          examples: ['个', '秒', '%', '点'],
        },
      },
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type VariableConfig = {
  variable_id: string
  name: string
  description?: string
  var_type: 'integer' | 'float' | 'string' | 'boolean' | 'array' | 'object'
  scope: 'global' | 'player' | 'npc' | 'faction' | 'location' | 'session'
  initial_value?: any
  min_value?: number
  max_value?: number
  allowed_values?: Array<string | number | boolean>
  persistent?: boolean
  sync_to_client?: boolean
  category?: string
  read_only?: boolean
  change_listeners?: Array<{
    event_id: string
    condition?: string
  }>
  reset_on?: 'never' | 'session_start' | 'game_start' | 'daily' | 'custom'
  format?: {
    display_format?: string
    unit?: string
  }
  metadata?: Record<string, any>
}
